<script setup lang="ts">
  import type { PropType } from 'vue';

  defineOptions({
    inheritAttrs: false,
  });

  type File = {
    display_name: string | null;
    filename: string;
    name: string;
    description: string | null;
    updated_at: string;
  }

  defineProps({
    header: { type: String },
    header_type: { type: String },
    content: { type: String },
    files: { type: Array as PropType<File[]>, required: true, default: [] },
  });
</script>
<template>
  <section class="download container section-wrapper">
    <component v-if="header" class="download__header" :is="header_type || 'h2'" v-html="header" />
    <div v-if="content" class="download__content section-typography" v-html="content" />
    <div class="download__files" v-if="files.length > 0">
      <article class="download__file" v-for="file in files">
        <h3 class="file__header">
          <VisitorLink :href="route('public.media.file.download', { filename: file.filename })" target="_blank" rel="noreferrer noopener" class="download__link">
            <IconFile aria-hidden="true" />
            {{ file.display_name || file.name }}
          </VisitorLink>
        </h3>
        <div v-if="file.description" class="file__content" v-html="file.description" />
      </article>
    </div>
  </section>
</template>
<style lang="scss">
  .download {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;

    &__header {
      position: relative;
      width: fit-content;
      font-size: 1.5rem;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 2px;
      }
    }

    &__content > p {
      font-size: 1rem;
    }

    &__files {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0 2rem;

      @media(max-width: 992px) {
        grid-template-columns: 1fr;
      }
    }

    &__file {
      display: flex;
      flex: 1 1;
      gap: 1rem;
      padding: 1rem;
      flex-direction: row;
      align-items: center;
      background-color: var(--color-primary-body-background);

      &:nth-child(4n+1),
      &:nth-child(4n+2) {
        background-color: var(--color-light-gray);
      }

      h1, h2, h3, h4, h5, h6 {
        font-size: 1rem;
        font-weight: var(--font-weight-400);
      }

      p {
        font-size: 0.875rem;
      }
    }

    &__link {
      display: flex;
      flex: 1 1;
      gap: 1rem;
      flex-direction: row;
      align-items: center;
      transition-property: color;
      transition-duration: var(--transition-primary-duration);
      transition-timing-function: var(--transition-primary-timing-function);
      font-weight: var(--font-weight-600);

      &:hover {
        color: var(--primary-color);
      }
    }
  }


  @media(max-width: 992px) {

    .download {
      width: 100%;
      padding: 0 0.625rem;

      &__header {
        font-size: 1rem;
      }

      &__file {
        gap: 0.625rem;
        padding: 0.625rem 1rem;

        &:nth-child(4n+1),
        &:nth-child(4n+2) {
          background-color: var(--color-primary-body-background);
        }

        &:nth-child(odd) {
          background-color: var(--color-primary-white);
        }
      }
    }
  }
</style>
